import { ExtractComponentProp } from '@approvalmax/types';
import { Form } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import type { OnChangeFieldRequiredForMatching } from 'pages/requestForm/containers/qbooks/QBooksBillForm/QBooksBillForm.types';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import {
    changeQbooksAccountLineItemAmount,
    changeQbooksAccountLineItemDescription,
    changeQbooksAccountLineItemsAccount,
    changeQbooksAccountLineItemsCustomer,
    changeQbooksAccountLineItemsTaxCode,
    changeQbooksLineAmountType,
    toggleQbooksAccountLineItemIsBillable,
    toggleQbooksAccountLineItemIsTaxable,
} from '../../../../actions';
import { getShowValidationErrors } from '../../../../selectors/pageSelectors';
import { getQBooksContext } from '../../../../selectors/qbooks';
import { getRequiredFields } from '../../../../selectors/requestSelectors';
import { ExpandedQBooksAccountLineItem } from '../../../../selectors/types/ExpandedQBooksAccountLineItem';
import { defaultOnChangeFieldRequiredForMatching } from '../../QBooksBillForm/QBooksBillForm.helpers';
import { createSchema } from '../QBooksAccountLineItemsSection.schema';
import { FormValues, Line } from '../QBooksAccountLineItemsSection.types';
import { useRequestExpanded } from './useRequest';

export const useQBooksAccountLineItemsForm = (params: {
    accountLineItems: ExpandedQBooksAccountLineItem[];
    hasOtherLineItems?: boolean;
    onChange: OnChangeFieldRequiredForMatching | typeof defaultOnChangeFieldRequiredForMatching;
}) => {
    const { accountLineItems, onChange } = params;

    const dispatch = useDispatch();
    const request = useRequestExpanded();
    const context = useSelector(getQBooksContext);
    const showValidationErrors = useSelector(getShowValidationErrors);

    const requiredFields = useSelector((state) => getRequiredFields(state, request));

    const isClassMandatory =
        context.hasClassFeature && context.classField && requiredFields.fieldIds.includes(context.classField.id);

    const isCustomerMandatory =
        context.hasCustomerFeature &&
        context.customerField &&
        requiredFields.fieldIds.includes(context.customerField.id);

    const schema = createSchema({
        showValidationErrors,
        isClassMandatory,
        isCustomerMandatory,
        isTrackingTaxesEnabled: context.isTrackingTaxesEnabled,
    });

    const lines = useMemo(() => {
        /**
         * This spreading is used to remove immutability,
         * otherwise lines ordering causes an error
         */
        return [
            ...accountLineItems.map<FormValues['lines'][number]>((line, index) => ({
                ...line,
                index,
                classId: line.class?.id ?? null,
                taxCodeId: line.taxCode?.id ?? null,
                accountId: line.account?.id ?? null,
                customerId: line.customer?.id ?? null,
            })),
        ];
    }, [accountLineItems]);

    const form = useForm<FormValues>({
        values: {
            lines,
            lineAmountType: request.details.lineAmountType ?? null,
        },
        mode: 'onChange',
        resolver: zodResolver(schema),
    });

    const handleFormChange: ExtractComponentProp<typeof Form<FormValues>, 'onChange'> = async (
        values,
        rootName,
        path
    ) => {
        const [_rootName, index, fieldName] = path;

        switch (rootName) {
            case 'lineAmountType': {
                handleChangeAmountType(values.lineAmountType);
                break;
            }

            case 'lines': {
                if (!index || !fieldName) {
                    break;
                }

                handleChangeLines(values.lines[index], fieldName);
                break;
            }
        }
    };

    const handleChangeAmountType = useCallback(
        (lineAmountType: domain.LineAmountType | null) => {
            if (lineAmountType) {
                dispatch(changeQbooksLineAmountType(lineAmountType));
            }
        },
        [dispatch]
    );

    const handleChangeLines = useCallback(
        (li: Line, fieldName: keyof FormValues['lines'][number]) => {
            switch (fieldName) {
                case 'account': {
                    onChange(
                        () => dispatch(changeQbooksAccountLineItemsAccount([li.id], (li.account as any) ?? null)),
                        {
                            isAccountLine: true,
                            lineId: li.id,
                        }
                    );
                    break;
                }

                case 'accountId': {
                    break;
                }

                case 'description': {
                    dispatch(changeQbooksAccountLineItemDescription(li.id, li.description));
                    break;
                }

                case 'taxCode': {
                    dispatch(
                        changeQbooksAccountLineItemsTaxCode([li.id], (li.taxCode as domain.QBooksTaxCode) ?? null)
                    );
                    break;
                }

                case 'taxCodeId': {
                    break;
                }

                case 'isBillable': {
                    dispatch(toggleQbooksAccountLineItemIsBillable(li.id, context.hasTaxableFeature, !!li.isBillable));
                    break;
                }

                case 'isTaxable': {
                    dispatch(toggleQbooksAccountLineItemIsTaxable(li.id, !!li.isTaxable));
                    break;
                }

                case 'customer': {
                    dispatch(changeQbooksAccountLineItemsCustomer([li.id], (li.customer as any) ?? null));
                    break;
                }

                case 'customerId': {
                    break;
                }

                case 'amount': {
                    if (li.amount && li.amount > 0) {
                        dispatch(changeQbooksAccountLineItemAmount(li.id, li.amount));
                    } else {
                        queueMicrotask(() => {
                            dispatch(changeQbooksAccountLineItemAmount(li.id, null));
                        });
                    }

                    break;
                }
            }
        },
        [context.hasTaxableFeature, dispatch, onChange]
    );

    return {
        form,
        lines,
        handleFormChange,
    };
};
